/**
 * Fingerprint utility wrapper for bundled fingerprint-datapicker-lib.js
 *
 * The fingerprint script is bundled directly into the SDK during build time.
 * No external network calls are made - the script is included in the bundle.
 */

// Type definition for the global __gateway object
declare global {
  interface Window {
    __gateway?: {
      getFingerprintString: () => string;
    };
  }
}

/**
 * Waits for DOM to be ready
 */
function waitForDOMReady(): Promise<void> {
  return new Promise((resolve) => {
    if (typeof document === 'undefined') {
      resolve();
      return;
    }

    if (document.readyState === 'complete' || document.readyState === 'interactive') {
      // DOM is already ready, wait a tick to ensure scripts have executed
      setTimeout(resolve, 0);
      return;
    }

    // Wait for DOMContentLoaded
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        // Wait a tick to ensure all scripts have executed
        setTimeout(resolve, 0);
      },
      { once: true }
    );
  });
}

/**
 * Checks if the fingerprint script is available
 */
function isFingerprintAvailable(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.__gateway !== 'undefined' &&
    typeof window.__gateway.getFingerprintString === 'function'
  );
}

/**
 * Gets the fingerprint string using the bundled fingerprint library
 * @returns Promise that resolves to the fingerprint string
 * @throws Error if fingerprint script fails to initialize or execute
 */
export async function getFingerprintAsync(): Promise<string> {
  // Check if we're in a browser environment
  if (typeof window === 'undefined') {
    throw new Error('getFingerprint requires a browser environment');
  }

  // Wait for DOM to be ready (fingerprint script initializes in docReady callback)
  await waitForDOMReady();

  // Get fingerprint string
  try {
    return window.__gateway!.getFingerprintString();
  } catch (error) {
    throw new Error(
      `Failed to get fingerprint: ${error instanceof Error ? error.message : 'Unknown error'}`
    );
  }
}

/**
 * Synchronous version - works if bundled script is already initialized and DOM is ready
 * Note: This will throw an error if DOM is not ready or script hasn't initialized yet.
 * For reliable usage, prefer getFingerprint() which handles async initialization.
 * @returns The fingerprint string
 * @throws Error if fingerprint script is not initialized or DOM is not ready
 */
export function getFingerprint(): string {
  if (typeof window === 'undefined') {
    throw new Error('getFingerprintSync requires a browser environment');
  }

  // Check if DOM is ready
  if (
    typeof document !== 'undefined' &&
    document.readyState !== 'complete' &&
    document.readyState !== 'interactive'
  ) {
    throw new Error('DOM is not ready. Use getFingerprint() instead or wait for DOMContentLoaded.');
  }

  if (!isFingerprintAvailable()) {
    throw new Error(
      'Fingerprint script is not initialized. Use getFingerprint() instead or ensure DOM is ready.'
    );
  }

  try {
    return window.__gateway!.getFingerprintString();
  } catch (error) {
    throw new Error(
      `Failed to get fingerprint: ${error instanceof Error ? error.message : 'Unknown error'}`
    );
  }
}
